import  { useState, useRef, useEffect } from "react";
import { X, ArrowUp, Loader2, MessageCircleDashed } from "lucide-react";
import { faqRag } from "../api";


export default function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "Hi! I'm EYES assistant. How can I help you today?",
      isBot: true,
      timestamp: new Date(),
    },
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Detect if user has scrolled to bottom
  useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      
      // Check if user is within 20px of the bottom
      const isBottom = windowHeight + scrollTop >= documentHeight - 20;
      setIsAtBottom(isBottom);
    };

    window.addEventListener('scroll', handleScroll);
    // Check initial position
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleChat = () => {
    setIsOpen(!isOpen);
  };

  const handleSendMessage = async () => {
    if (message.trim() && !isLoading) {
      const userMessage = {
        id: Date.now(),
        text: message,
        isBot: false,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, userMessage]);
      setMessage("");
      setIsLoading(true);

      try {
        // Call the API
        const response = await faqRag(message, 5);

        const botResponse = {
          id: Date.now() + 1,
          text: response.answer,
          isBot: true,
          timestamp: new Date(),
        };

        setMessages((prev) => [...prev, botResponse]);
      } catch (error) {
        const errorMessage = {
          id: Date.now() + 1,
          text:
            error instanceof Error
              ? error.message
              : "Sorry, I couldn't process your request. Please try again.",
          isBot: true,
          timestamp: new Date(),
          isError: true,
        };

        setMessages((prev) => [...prev, errorMessage]);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Custom markdown renderer for tables
  const renderMarkdown = (text) => {
    const lines = text.split("\n");
    const elements = [];
    let i = 0;
    let key = 0;

    while (i < lines.length) {
      const line = lines[i];

      if (line.trim().startsWith("|")) {
        const tableLines = [];
        while (i < lines.length && lines[i].trim().startsWith("|")) {
          tableLines.push(lines[i]);
          i++;
        }

        if (tableLines.length >= 2) {

          const headers = tableLines[0]
            .split("|")
            .filter((cell) => cell.trim())
            .map((cell) => cell.trim());
          const rows = tableLines.slice(2).map((row) =>
            row
              .split("|")
              .filter((cell) => cell.trim())
              .map((cell) => cell.trim())
          );

          elements.push(
            <div key={key++} className="overflow-x-auto my-3">
              <table className="min-w-full border-collapse border border-gray-300 text-xs">
                <thead className="bg-gray-100">
                  <tr>
                    {headers.map((header, idx) => (
                      <th
                        key={idx}
                        className="border border-gray-300 px-2 py-1.5 text-left font-semibold"
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, rowIdx) => (
                    <tr key={rowIdx} className="hover:bg-gray-50">
                      {row.map((cell, cellIdx) => (
                        <td
                          key={cellIdx}
                          className="border border-gray-300 px-2 py-1.5"
                          dangerouslySetInnerHTML={{
                            __html: cell.replace(/<br>/g, "<br/>"),
                          }}
                        />
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
      } else {
        // Regular text line
        if (line.trim()) {
          // Handle bold text **text**
          let processedLine = line.replace(
            /\*\*(.*?)\*\*/g,
            "<strong>$1</strong>"
          );
          // Handle italic text *text*
          processedLine = processedLine.replace(/\*(.*?)\*/g, "<em>$1</em>");

          elements.push(
            <p
              key={key++}
              className="mb-2"
              dangerouslySetInnerHTML={{ __html: processedLine }}
            />
          );
        } else {
          elements.push(<br key={key++} />);
        }
        i++;
      }
    }

    return <div>{elements}</div>;
  };

  return (
    <>
      {/* Chat Window - moves UP when at bottom to avoid covering footer */}
      <div
        className={`fixed right-4 sm:right-6 w-[90vw] sm:w-96 max-w-md z-50 transition-all duration-300 ease-in-out ${
          isAtBottom 
            ? 'bottom-32 sm:bottom-36' // Higher when at bottom
            : 'bottom-24 sm:bottom-28'  // Normal position
        } ${
          isOpen
            ? "opacity-100 translate-y-0 pointer-events-auto"
            : "opacity-0 translate-y-4 pointer-events-none"
        }`}
      >
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-200">
          {/* Header */}
          <div className="bg-white border-b-2 border-green-700 text-white p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex items-center">
                <span className="mr-1.5 w-2 h-2 bg-green-500 rounded-full animate-pulse inline-block"></span>
                <h2 className="text-green-600 text-base sm:text-lg font-semibold">EYES Assistant</h2>
              </div>
            </div>

            <button
              onClick={toggleChat}
              className="text-white bg-green-800 hover:bg-green-700 rounded-full p-2 transition-colors"
              aria-label="Close chat"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="h-64 sm:h-80 overflow-y-auto p-4 bg-gray-50 space-y-4">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${
                  msg.isBot ? "justify-start" : "justify-end"
                }`}
              >
                <div
                  className={`max-w-[85%] sm:max-w-[75%] px-4 py-3 rounded-2xl ${
                    msg.isBot
                      ? msg.isError
                        ? "bg-red-50 text-red-800 shadow-sm rounded-tl-none border border-red-200"
                        : "bg-white text-gray-800 shadow-sm rounded-tl-none"
                      : "bg-green-900 text-white rounded-tr-none"
                  }`}
                >
                  {msg.isBot ? (
                    <div className="text-sm leading-relaxed">
                      {renderMarkdown(msg.text)}
                    </div>
                  ) : (
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">
                      {msg.text}
                    </p>
                  )}

                  <p className="text-xs mt-1 opacity-60">
                    {msg.timestamp.toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </p>
                </div>
              </div>
            ))}

            {/* Loading indicator */}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white text-gray-800 shadow-sm rounded-2xl rounded-tl-none px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">Typing...</span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 bg-white border-t border-gray-200">
            <div className="flex space-x-2">
              <input
                type="text"
                placeholder="Type a message..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isLoading}
                className="flex-1 border border-gray-300 text-black focus:border-green-900 focus:ring-1 focus:ring-green-900 rounded-full px-4 py-2 text-sm disabled:opacity-50 outline-none"
              />
              <button
                onClick={handleSendMessage}
                disabled={isLoading || !message.trim()}
                className="bg-green-900 hover:bg-green-800 text-white rounded-full w-10 h-10 p-0 flex items-center justify-center shrink-0 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <ArrowUp className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <button
        onClick={toggleChat}
        className={`fixed right-4 sm:right-6 w-14 h-14 sm:w-16 sm:h-16 text-white bg-green-800 hover:bg-green-700 rounded-full shadow-2xl hover:shadow-3xl hover:scale-110 transition-all duration-300 ease-in-out z-50 flex items-center justify-center ${
          isAtBottom 
            ? 'bottom-16 sm:bottom-20' // Higher position when at bottom
            : 'bottom-4 sm:bottom-6'   // Normal position
        } ${
          isOpen ? "rotate-180" : ""
        }`}
        aria-label={isOpen ? "Close chat" : "Open chat"}
      >
        {isOpen ? (
          <X className="w-6 h-6 sm:w-7 sm:h-7" />
        ) : (
          <MessageCircleDashed className="w-6 h-6 sm:w-7 sm:h-7" />
        )}
      </button>

    
      {!isOpen && (
        <div 
          className={`fixed right-4 sm:right-6 z-40 transition-all duration-300 ease-in-out ${
            isAtBottom 
              ? 'bottom-32 sm:bottom-36' // Higher when at bottom
              : 'bottom-20 sm:bottom-24' // Normal position
          }`}
        >
          <div className="relative bg-white/95 backdrop-blur-sm px-4 py-2.5 rounded-xl shadow-xl border-2 border-green-700 text-xs sm:text-sm text-gray-900 font-medium flex items-center gap-2">
            <span className="text-lg">👋</span>
            <span>Need help?</span>
            <div className="absolute -bottom-1.5 right-8 w-3 h-3 bg-white/95 border-r-2 border-b-2 border-green-700 rotate-45"></div>
          </div>
        </div>
      )}
    </>
  );
}